import type { Alert } from "./alerts";
import { REPAIR_RATE_REVIEW, repairRateExceedsReview, type GraduationResult } from "./graduation";
import type { GraduationClass } from "./types";

/**
 * The autonomy state machine: graduation, auto-demote, kill switch.
 *
 * `graduation.ts` answers "has this scenario qualified", and stops there on
 * purpose: "Nothing here graduates anything. It reports eligibility, and that
 * is the whole contract." This module is the thing that was missing on the
 * other side of that sentence, the part that says what a scenario's standing
 * IS and what moves it.
 *
 * ONE ASYMMETRY RUNS THROUGH ALL OF IT, and it is B6's:
 *
 *   "Thresholds qualify; the HUMAN flips the switch, always.
 *    Auto-demote stays automatic."
 *
 * So promotion requires a named person and cannot be reached by any number of
 * good weeks, and demotion requires nobody and happens on a metric. Anything
 * in this file that looks asymmetric is that ruling, not an oversight.
 *
 * Pure. No I/O, no clock of its own, no store. The caller supplies `now`,
 * persists the new standing, and hands the alert to the sink. A state machine
 * that reads its own database is a state machine that cannot be tested at the
 * boundaries that matter.
 */

/**
 * Planning elements #5's staged rollout, verbatim: "shadow -> draft-mode ->
 * supervised -> graduated is our native version of canary deployment".
 *
 * AMBIGUITY, LOGGED AS TA-89. Elsewhere the vault uses "supervised" and
 * "draft mode" interchangeably ("Graduation (supervised -> auto-send)"), so
 * whether they are two rungs or one name for one rung is not settled. Both
 * readings agree on the only thing that is load-bearing, which is the next
 * function down: exactly one rung sends without a human. The rest is what the
 * owner's board says, so the conservative choice is to keep both rungs and
 * never let a transition land higher than its ruling names.
 */
export const AUTONOMY_LADDER = ["shadow", "draft", "supervised", "graduated"] as const;

export type AutonomyState = (typeof AUTONOMY_LADDER)[number];

/**
 * The whole safety question for a RUNG, in one line. It knows nothing about
 * the contact, so it is not the send-path question: that is
 * `mayTransmitWithoutAHuman` below. Kept one-argument so the ladder can be
 * filtered by it.
 */
export function transmitsWithoutAHuman(state: AutonomyState): boolean {
  return state === "graduated";
}

/**
 * What the send path must know about the CONTACT before any rung may send
 * without a human.
 *
 * `null` is honest for "nobody has computed this contact's VIP status yet".
 * The field is required on purpose: an absent flag is not a `false` flag.
 */
export interface TransmitContact {
  readonly contactVip: boolean | null;
}

export type AutonomousTransmitRefusal =
  | "vip_carve_out"
  | "vip_status_unknown"
  | "autonomy_state_unknown"
  | "rung_requires_human";

export interface AutonomousTransmitDecision {
  readonly transmits: boolean;
  /** Null only when `transmits` is true. */
  readonly refusal: AutonomousTransmitRefusal | null;
  /** Names the cause and what to do, for the draft rail and the ledger. */
  readonly message: string;
}

/**
 * The VIP carve-out, Master Spec Part B intro, verbatim in substance: "top
 * clients ... stay on Mike's personal touch: the agent drafts and briefs, Mike
 * sends." Inbox Composer routes the same threads "always the owner".
 *
 * So a VIP contact never transmits without a human on ANY rung, graduated
 * included. Graduation is per agent x scenario (B6) and says nothing about
 * who the customer is. VIP changes service level and routing, never a number
 * (CLAUDE.md section 3): this is the routing half, and it touches no figure.
 *
 * Order of refusals: VIP first, because it holds on every rung and is the
 * reason the owner most needs to see. Unknown status next: when unsure,
 * don't (B13). A contact whose VIP status was never computed may be one of
 * Mike's, and defaulting it to non-VIP is the silent path section 0 forbids.
 * A state that is not on the ladder next, so its message names the bad state
 * and not a rung that does not exist. The rung last.
 *
 * The unknown-VIP message says which unknown it is, because the fix differs:
 * null is the honest "not computed yet" and clears when the flag is set, an
 * absent field is a caller bug, and any other type is a malformed row.
 */
export function autonomousTransmitDecision(
  state: AutonomyState,
  contact: TransmitContact,
): AutonomousTransmitDecision {
  // Runtime check, not only the type: a JSON row or a spread that dropped the
  // field arrives as `undefined`, and anything other than a real boolean is
  // unknown.
  const vip: unknown = contact?.contactVip;
  if (vip === true) {
    return {
      transmits: false,
      refusal: "vip_carve_out",
      message: "VIP contact: the agent drafts and briefs, the owner sends. No rung auto-sends to a VIP.",
    };
  }
  if (vip !== false) {
    const held = "so it is treated as possibly VIP and held for a human.";
    const message =
      vip === null
        ? `VIP status for this contact has not been computed yet, ${held} It clears once vip-signal-detection (Agent Brains #12) or the owner sets the flag.`
        : vip === undefined
          ? `The caller did not supply contactVip, ${held} Pass true, false, or null for a contact nobody has scored yet.`
          : `contactVip is malformed (a ${typeof vip}, not a boolean or null), ${held} Fix the row or the mapping that produced it.`;
    return { transmits: false, refusal: "vip_status_unknown", message };
  }
  // Same reason as the contactVip check: an untyped caller or a stored string
  // in the wrong case must not be refused as though it named a real rung.
  if (!(AUTONOMY_LADDER as readonly unknown[]).includes(state)) {
    return {
      transmits: false,
      refusal: "autonomy_state_unknown",
      message: `"${String(state)}" is not an autonomy state (expected one of ${AUTONOMY_LADDER.join(", ")}), so this thread is held for a human. Fix the stored standing.`,
    };
  }
  if (!transmitsWithoutAHuman(state)) {
    return {
      transmits: false,
      refusal: "rung_requires_human",
      message: `Scenario is at ${state}: only graduated transmits without a human.`,
    };
  }
  return { transmits: true, refusal: null, message: "Graduated scenario, non-VIP contact." };
}

/** The send-path predicate: rung AND contact. False whenever the decision refuses. */
export function mayTransmitWithoutAHuman(state: AutonomyState, contact: TransmitContact): boolean {
  return autonomousTransmitDecision(state, contact).transmits;
}

const rung = (state: AutonomyState): number => AUTONOMY_LADDER.indexOf(state);

/** Never above `ceiling`, whatever the ruling's wording. */
const clamp = (state: AutonomyState, ceiling: AutonomyState): AutonomyState =>
  rung(state) <= rung(ceiling) ? state : ceiling;

// ---------------------------------------------------------------------------
// Agent Brains section 9: the class bars
// ---------------------------------------------------------------------------

export interface ClassBars {
  /** Null for D: no volume of drafts graduates it. */
  readonly minDrafts: number | null;
  readonly approvalFloor: number | null;
  /** Section 9 names it on C only. B6's human switch applies to all four. */
  readonly ownerSignOffRequired: boolean;
  readonly graduates: boolean;
}

/**
 * "A = auto-send eligible at ~50 drafts @95% - B = ~100 @97% - C = ~200 @98% +
 * owner sign-off - D = never fully automated (human always in loop)."
 *
 * Transcribed, not chosen. Until now `GraduationInput` took `minDrafts` and
 * `approvalFloor` from whoever called it, which meant the bars lived in each
 * caller and section 9's table lived nowhere.
 */
export const CLASS_BARS: Readonly<Record<GraduationClass, ClassBars>> = {
  A: { minDrafts: 50, approvalFloor: 0.95, ownerSignOffRequired: false, graduates: true },
  B: { minDrafts: 100, approvalFloor: 0.97, ownerSignOffRequired: false, graduates: true },
  C: { minDrafts: 200, approvalFloor: 0.98, ownerSignOffRequired: true, graduates: true },
  D: { minDrafts: null, approvalFloor: null, ownerSignOffRequired: true, graduates: false },
};

const NEXT_CLASS_DOWN: Readonly<Record<GraduationClass, GraduationClass>> = {
  A: "B",
  B: "C",
  C: "D",
  // The floor, and not a hole: D is where "human always in loop" lives.
  D: "D",
};

// ---------------------------------------------------------------------------
// How many periods is "sustained"
// ---------------------------------------------------------------------------

/**
 * F-25: "persistently >10% triggers review, then auto-demote". Two levels,
 * and the ruling names neither interval.
 *
 * v1 = 2 consecutive review periods, where a period is one weekly synthesis
 * run (B4's cadence, the only period this system actually has). Chosen in the
 * direction section 0 requires: a scenario demoted a week early costs the
 * owner some clicking, a scenario left autonomous a week too long spends
 * customer trust. AWAITING RATIFICATION (TA-89), like the window turn count
 * before it.
 */
export const REPAIR_SUSTAINED_PERIODS = 2;

/**
 * Section 12: "Repeated floor breach in a scenario | Auto-demote that
 * scenario one class (B6) + alert with samples". "Repeated" is likewise
 * unnumbered; same v1 value, same reasoning, same ratification.
 */
export const FLOOR_BREACH_PERIODS = 2;

// ---------------------------------------------------------------------------
// Standing, triggers, transitions
// ---------------------------------------------------------------------------

export interface AutonomyStanding {
  /** Per agent x scenario type (B6). One standing, one scenario. */
  readonly scenario: string;
  readonly state: AutonomyState;
  /**
   * The class this scenario is being HELD to, which starts as the section 9
   * map's value and only ever gets stricter from here.
   *
   * The map's class D rows are T3 and this module never writes the map. A
   * demotion writes the standing, which can restrict what the map allows and
   * can never widen it, and a human restores it. Logged as TA-89.
   */
  readonly graduationClass: GraduationClass;
}

export type AutonomyTrigger =
  /** The weekly qualification check. Reports, never moves. */
  | { readonly kind: "eligibility_review"; readonly result: GraduationResult }
  | { readonly kind: "approval_rate_drop"; readonly approvalRate: number }
  | {
      readonly kind: "repair_rate_high";
      /**
       * TA-251: this period's counts, never a rate. A rate someone rounded
       * for display shows 21 repaired of 209 as exactly the bar, and `<=`
       * read that as within it. The bar is decided on these counts by
       * `repairRateExceedsReview`, which throws on a pair that is not counts.
       */
      readonly repairedDrafts: number;
      readonly decidedDrafts: number;
      readonly consecutivePeriods: number;
    }
  | {
      readonly kind: "floor_breach";
      readonly floor: string;
      readonly consecutivePeriods: number;
      /** Section 12: "alert with samples". Draft ids, never draft text. */
      readonly sampleDraftIds: readonly string[];
    }
  | { readonly kind: "kill_switch"; readonly by: string }
  | {
      readonly kind: "human_promotion";
      readonly by: string;
      readonly to: AutonomyState;
      /** Null is honest for a move that is not to `graduated`. */
      readonly eligibility: GraduationResult | null;
      readonly ownerSignOff?: boolean;
    };

export interface AutonomyTransition {
  readonly from: AutonomyState;
  readonly to: AutonomyState;
  readonly classFrom: GraduationClass;
  readonly classTo: GraduationClass;
  readonly changed: boolean;
  /** True when no human was in it. Only demotions are ever true. */
  readonly automatic: boolean;
  /** Machine reason, for the ledger row. */
  readonly reason: string;
  /** Set when a requested promotion was refused, null otherwise. */
  readonly refusal: string | null;
  /**
   * Null only when nothing happened that an operator needs to know. Every
   * automatic demotion raises one: B6 says "owner notified" and section 0
   * says a degraded system is loud to the operator.
   */
  readonly alert: Alert | null;
}

interface AlertDraft {
  readonly kind: string;
  readonly message: string;
  readonly detail?: Readonly<Record<string, unknown>>;
}

interface SettleOpts {
  readonly automatic?: boolean;
  readonly refusal?: string | null;
  readonly alert?: AlertDraft | null;
}

export function applyAutonomyTrigger(
  standing: AutonomyStanding,
  trigger: AutonomyTrigger,
  now: Date,
): AutonomyTransition {
  const from = standing.state;
  const classFrom = standing.graduationClass;

  const settle = (
    to: AutonomyState,
    classTo: GraduationClass,
    reason: string,
    opts: SettleOpts = {},
  ): AutonomyTransition => {
    const changed = to !== from || classTo !== classFrom;
    const a = opts.alert ?? null;
    return {
      from,
      to,
      classFrom,
      classTo,
      changed,
      automatic: opts.automatic ?? false,
      reason,
      refusal: opts.refusal ?? null,
      alert:
        a === null
          ? null
          : {
              kind: a.kind,
              // A standing fact about this scenario's move, not an occurrence
              // with its own identity: replaying the trigger must land one row
              // (section 5). The caller adds its own client and agent.
              key: `autonomy:${standing.scenario}:${reason}:${from}:${to}:${classFrom}:${classTo}`,
              message: a.message,
              at: now,
              ...(a.detail ? { detail: a.detail } : {}),
            },
    };
  };

  const stay = (reason: string, opts: SettleOpts = {}): AutonomyTransition =>
    settle(from, classFrom, reason, opts);

  switch (trigger.kind) {
    case "eligibility_review": {
      if (!trigger.result.eligible) return stay("not_eligible");
      // Qualified, and that is all it is. The switch stays where it was.
      return stay("eligible_pending_human", {
        alert: {
          kind: "graduation_eligible",
          message: `${standing.scenario} has qualified for auto-send (approval ${trigger.result.approvalRate}). A human flips the switch.`,
          detail: { scenario: standing.scenario, approvalRate: trigger.result.approvalRate, state: from },
        },
      });
    }

    case "human_promotion": {
      const by = trigger.by.trim();
      const refuse = (refusal: string, message: string): AutonomyTransition =>
        stay("promotion_refused", {
          refusal,
          alert: { kind: "autonomy_promotion_refused", message, detail: { scenario: standing.scenario, refusal } },
        });

      // Section 5: every action in this system is attributable. An unnamed
      // switch flip is the one thing a graduation record cannot be missing.
      if (by.length === 0) {
        return refuse("unattributed_promotion", `Promotion of ${standing.scenario} refused: no human named.`);
      }
      if (rung(trigger.to) <= rung(from)) {
        // Demotions have their own reasons, their own alerts and their own
        // automatic flag. Honouring one here would file it as a promotion.
        return refuse("not_a_promotion", `${standing.scenario} is already at ${from}.`);
      }
      if (trigger.to === "graduated") {
        if (!CLASS_BARS[classFrom].graduates) {
          return refuse(
            "class_d_never_graduates",
            `${standing.scenario} is class ${classFrom}: a human is always in the loop.`,
          );
        }
        if (!trigger.eligibility?.eligible) {
          return refuse(
            "thresholds_not_met",
            `${standing.scenario} has not qualified for auto-send. The switch is not a bypass.`,
          );
        }
        if (CLASS_BARS[classFrom].ownerSignOffRequired && trigger.ownerSignOff !== true) {
          return refuse(
            "owner_sign_off_required",
            `Class ${classFrom} needs the owner's explicit sign-off as well as the thresholds.`,
          );
        }
      }
      return settle(trigger.to, classFrom, "promoted", {
        alert: {
          kind: "autonomy_promoted",
          message: `${standing.scenario} moved ${from} to ${trigger.to} by ${by}.`,
          detail: { scenario: standing.scenario, by, from, to: trigger.to },
        },
      });
    }

    case "approval_rate_drop": {
      // B6 verbatim: "approval rate drops -> back to draft mode, owner
      // notified". Nobody approves this and nothing waits on a human.
      const to = clamp(from, "draft");
      if (to === from) return stay("already_below_autonomy");
      return settle(to, classFrom, "approval_rate_below_floor", {
        automatic: true,
        alert: {
          kind: "autonomy_demoted",
          message: `${standing.scenario} demoted ${from} to ${to}: approval rate ${trigger.approvalRate}.`,
          detail: { scenario: standing.scenario, approvalRate: trigger.approvalRate, from, to },
        },
      });
    }

    case "repair_rate_high": {
      // TA-251: F-25's one bar (graduation.ts), on exact counts. Strictly
      // greater, so exactly the bar stays within it, as `<=` always read.
      if (!repairRateExceedsReview(trigger.repairedDrafts, trigger.decidedDrafts)) {
        return stay("repair_rate_within_bar");
      }
      const repairs = `${trigger.repairedDrafts} repaired of ${trigger.decidedDrafts} decided`;
      if (trigger.consecutivePeriods < REPAIR_SUSTAINED_PERIODS) {
        // "Persistently >10% triggers review, THEN auto-demote." The first
        // level is a human reading samples, not a state change.
        return stay("repair_rate_review", {
          alert: {
            kind: "repair_rate_review",
            message: `${standing.scenario} repair rate ${repairs} is over the ${REPAIR_RATE_REVIEW} bar. Review, not yet a demotion.`,
            detail: {
              scenario: standing.scenario,
              repairedDrafts: trigger.repairedDrafts,
              decidedDrafts: trigger.decidedDrafts,
              consecutivePeriods: trigger.consecutivePeriods,
            },
          },
        });
      }
      const to = clamp(from, "draft");
      return settle(to, classFrom, "repair_rate_sustained", {
        automatic: true,
        alert: {
          kind: "autonomy_demoted",
          message: `${standing.scenario} demoted ${from} to ${to}: repair rate ${repairs} sustained over ${trigger.consecutivePeriods} periods.`,
          detail: {
            scenario: standing.scenario,
            repairedDrafts: trigger.repairedDrafts,
            decidedDrafts: trigger.decidedDrafts,
            consecutivePeriods: trigger.consecutivePeriods,
            from,
            to,
          },
        },
      });
    }

    case "floor_breach": {
      const detail = {
        scenario: standing.scenario,
        floor: trigger.floor,
        consecutivePeriods: trigger.consecutivePeriods,
        sampleDraftIds: trigger.sampleDraftIds,
      };
      if (trigger.consecutivePeriods < FLOOR_BREACH_PERIODS) {
        return stay("floor_breach_first", {
          alert: {
            kind: "floor_breach",
            message: `${standing.scenario} breached the ${trigger.floor} floor. Section 12 demotes on a repeated breach.`,
            detail,
          },
        });
      }
      const classTo = NEXT_CLASS_DOWN[classFrom];
      // The class demotion raises the bar this scenario has to clear, so its
      // current standing was earned against a bar that no longer applies. It
      // requalifies from draft, which is where B6 sends every demotion.
      const to = clamp(from, "draft");
      return settle(to, classTo, "floor_breach_repeated", {
        automatic: true,
        alert: {
          kind: "autonomy_class_demoted",
          message: `${standing.scenario} demoted class ${classFrom} to ${classTo} and ${from} to ${to}: repeated ${trigger.floor} floor breach.`,
          detail: { ...detail, classFrom, classTo, from, to },
        },
      });
    }

    case "kill_switch": {
      // B6: "One-click kill switch -> everything back to supervised". Read
      // literally that would RAISE a scenario sitting in draft after an
      // auto-demote, so it clamps: the switch only ever takes autonomy away.
      const to = clamp(from, "supervised");
      return settle(to, classFrom, "kill_switch", {
        alert: {
          kind: "kill_switch_pulled",
          message: `${standing.scenario} held at ${to} by ${trigger.by}: kill switch.`,
          detail: { scenario: standing.scenario, by: trigger.by, from, to },
        },
      });
    }
  }
}
